import { Flex, Space, Typography } from "../ui";
import type { CSSProperties, ReactNode } from "react";
import { PageHeaderBackArrow, PageHeaderBackRow } from "./PageHeaderBack";
import { spacing } from "../settings/settingsListDesign";
import { PageTitle } from "../settings/SettingsListPageTitle";
import { usePageHeaderBack } from "./pageHeaderNavigation";
import { usePageMetadata } from "./pageMetadata";

export interface PageHeaderProps {
  /** Statischer Titel, falls der Host keinen kanonischen Katalogeintrag besitzt. */
  title?: ReactNode;
  /** Fachlich dynamischer Objekt-/Prozesstitel; ersetzt die statische Katalogbezeichnung. */
  objectTitle?: ReactNode;
  titleIcon?: ReactNode;
  /** Optionaler Untertitel / Kurzbeschreibung unter dem Titel. */
  subtitle?: ReactNode;
  /** Untertitel unter Titel und Aktionen über die gesamte Kopfbreite anzeigen. */
  subtitleFullWidth?: boolean;
  /**
   * Inline-Slot direkt neben dem Titel (z. B. Help-Link, Status-Tag).
   * Sitzt in derselben Zeile wie der Titel, links von `extra`.
   */
  titleSuffix?: ReactNode;
  /** Rechtsbündige Actions (Buttons, Selektoren). */
  extra?: ReactNode;
  /** Optionaler Slot oberhalb des Titels (Breadcrumbs, Back-Link). */
  breadcrumb?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * Einheitlicher Seitenkopf für mQorva-Pages.
 *
 * Ein Seitenkopf für alle Produkte (14.10.2026): derselbe Aufbau wie der Kopf der Listenseiten
 * (`page-header-main` mit `PageTitle`), damit Titelschrift und Ornament überall aus dem Theme kommen.
 * Aktionen rechts. Ohne eigenen Außenabstand — den setzt der umschließende `PageContainer`.
 * Standardseiten verwenden `<PageFrame>`; spezialisierte Arbeitsflächen setzen diesen Renderer direkt ein.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title={tr("Campaigns")}
 *   subtitle={tr("Manage your active ad runs")}
 *   titleSuffix={<AppHelpLink topic="campaigns" />}
 *   extra={<Button type="primary" icon={<PlusOutlined />}>{tr("New campaign")}</Button>}
 * />
 * ```
 */
export function PageHeader({
  title,
  objectTitle,
  titleIcon,
  subtitle,
  subtitleFullWidth = false,
  titleSuffix,
  extra,
  breadcrumb,
  className,
  style,
}: PageHeaderProps) {
  const metadata = usePageMetadata();
  const resolvedTitle = objectTitle !== undefined ? objectTitle : metadata?.title ?? title;
  const resolvedExtra = extra === undefined ? metadata?.extra : extra;
  const resolvedIcon = titleIcon ?? metadata?.icon;
  const back = usePageHeaderBack();
  return (
    <Flex
      align="flex-start"
      justify="space-between"
      wrap="wrap"
      gap={spacing.elementGap}
      className={className ? `mq-page-header ${className}` : "mq-page-header"}
      data-page-heading="primary"
      style={{ flexShrink: 0, ...style }}
    >
      <div className="page-header-main" style={{ minWidth: 0 }}>
        {back ? <PageHeaderBackRow back={back} /> : null}
        {breadcrumb ? <div className="mq-page-header-breadcrumb">{breadcrumb}</div> : null}
        <div className={back ? "mq-page-header-title mq-page-header-title--with-back" : "mq-page-header-title"}>
          {back ? <PageHeaderBackArrow back={back} /> : null}
          <Flex align="center" gap={spacing.elementGap} wrap="wrap" style={{ minWidth: 0 }}><PageTitle>{resolvedIcon ? <Space size={spacing.elementGap}><span aria-hidden="true">{resolvedIcon}</span>{resolvedTitle}</Space> : resolvedTitle}</PageTitle>{titleSuffix}</Flex>
        </div>
        {subtitle && !subtitleFullWidth ? (
          <Typography.Paragraph type="secondary" style={{ marginTop: spacing.xs, marginBottom: 0 }}>
            {subtitle}
          </Typography.Paragraph>
        ) : null}
      </div>
      {resolvedExtra ? <div className="page-header-extra mq-page-header-actions">{resolvedExtra}</div> : null}
      {subtitle && subtitleFullWidth ? (
        <Typography.Paragraph
          className="mq-page-header-subtitle"
          type="secondary"
          style={{ flex: "1 1 100%", minWidth: 0, maxWidth: "none", marginTop: spacing.xs, marginBottom: 0 }}
        >
          {subtitle}
        </Typography.Paragraph>
      ) : null}
    </Flex>
  );
}
